import { ChangeDetectionStrategy, Component, DestroyRef, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize, timer } from 'rxjs';
import { VchasnoKasaService } from '../../services/vchasno-kasa.service';
import { BoardPaymentService } from '../../services/board-payment.service';
import { canRetryFiscal, FiscalOperation } from './prro.types';
import { fiscalReportView, FiscalReportView, ReportPayment, ReportTax } from './fiscal-report.view';
import { fiscalOperationView, FiscalOperationView } from './fiscal-operation.view';
import { TableV2Column } from '../table-v2/table-v2.interface';

@Component({
  selector: 'app-fiscal-operation-detail',
  standalone: false,
  templateUrl: './fiscal-operation-detail.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  host: { class: 'block h-full min-h-0' },
})
export class FiscalOperationDetailComponent implements OnInit {
  @Input({ required: true }) operationId!: string;
  @Input() registerName = '';
  @Output() close = new EventEmitter<boolean>();
  private readonly api = inject(VchasnoKasaService);
  private readonly feedback = inject(BoardPaymentService);
  private readonly destroyRef = inject(DestroyRef);
  private changed = false;
  private inFlight = false;
  protected readonly canRetry = canRetryFiscal;
  protected detail: FiscalOperation | null = null;
  protected report: FiscalReportView = null;
  protected summary: FiscalOperationView | null = null;
  protected get productColumns(): TableV2Column<FiscalOperationView['rows'][number]>[] { return [
    { key: 'name', header: 'prro.operation.products' },
    { key: 'quantity', header: 'prro.operation.quantity', align: 'right' },
    { key: 'price', header: 'prro.operation.price', align: 'right' },
    { key: 'discount', header: this.summary?.phase === 'final' ? 'prro.settlement.discount_credit' : 'prro.discount', align: 'right' },
    { key: 'total', header: 'prro.total', align: 'right' },
  ]; }
  protected readonly receiptPaymentColumns: TableV2Column<FiscalOperationView['payments'][number]>[] = [
    { key: 'name', header: 'prro.payment_method' },
    { key: 'amount', header: 'prro.total', align: 'right' },
  ];
  protected readonly paymentColumns: TableV2Column<ReportPayment>[] = [
    { key: 'name', header: 'prro.payment_method' },
    { key: 'sales', header: 'prro.report.sales', align: 'right' },
    { key: 'returns', header: 'prro.report.returns', align: 'right' },
  ];
  protected readonly taxColumns: TableV2Column<ReportTax>[] = [
    { key: 'name', header: 'prro.tax_group' },
    { key: 'sales', header: 'prro.report.sales_base', align: 'right' },
    { key: 'returns', header: 'prro.report.return_base', align: 'right' },
  ];
  protected loading = true;
  protected error = '';
  protected busy = false;

  protected get waitingForShift(): boolean {
    return this.detail?.error?.['code'] === 'FISCAL_RECEIPT_SHIFT_PENDING';
  }

  ngOnInit(): void {
    this.load();
    timer(10000, 10000)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (!this.busy && !this.loading && !this.error) this.load(false);
      });
  }

  requestClose(): void {
    if (!this.busy) this.close.emit(this.changed);
  }

  protected load(showLoading = true): void {
    if (this.inFlight || this.busy) return;
    this.inFlight = true;
    this.loading = showLoading;
    this.error = '';
    this.api.operation(this.operationId)
      .pipe(
        finalize(() => { this.loading = false; this.inFlight = false; }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (detail) => this.setDetail(detail),
        error: (error) => {
          if (showLoading) this.error = error?.status === 403 ? 'prro.denied' : 'prro.load_failed';
        },
      });
  }

  private setDetail(detail: FiscalOperation): void {
    this.detail = detail;
    this.summary = fiscalOperationView(detail);
    this.report = detail.task === 10 || detail.task === 11 ? fiscalReportView(detail.response?.['info']) : null;
  }

  protected retry(): void {
    if (this.busy || this.inFlight || !this.detail || !this.canRetry(this.detail)) return;
    this.busy = true;
    const request = [1, 2].includes(this.detail.task ?? -1) ? this.api.retryReceipt(this.detail) : this.api.retry(this.operationId);
    request
      .pipe(finalize(() => (this.busy = false)), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (detail) => { this.setDetail(detail); this.changed = true; },
        error: (error) => this.feedback.error(error),
      });
  }
}
